import gql from 'graphql-tag';
import { client } from './client';
import { COMMENT_FIELDS } from './task.fragment';

export const NOTE_FIELDS = gql`
  ${COMMENT_FIELDS}
  fragment noteInfo on NoteSchema {
    id
    weddingId
    categoryId
    createdAt
    createdBy {
      id
      fullname
      profileImage {
        resolutions
      }
      initials
    }
    updatedAt
    updatedBy {
      id
      fullname
      profileImage {
        resolutions
      }
      initials
    }
    title
    noteHtml
    noteJson
    url
    plannerOnly
    clientReadOnly
    isShortcut
    showBrandingHeader
    blockedAt
    image {
      key
      status
      cropPosition
    }
    originalImage {
      key
      status
    }
    imageUrls
    order
    groupId
    isFileUpload
    fileViewerUrl
    file {
      id
      originalFilename
      ext
    }
    comments {
      ...commentInfo
    }
    noteFileId
    isBlocking
    isMigrated
    blockedBy {
      id
      email
      fullname
    }
  }
`;

export const NOTE_TEMPLATE_FIELDS = gql`
  fragment noteTemplateInfo on NoteTemplateSchema {
    id
    name
    organizationId
    categoryId
    title
    noteHtml
    noteJson
    plannerOnly
    clientReadOnly
    createdAt
    createdBy {
      id
      fullname
      profileImage {
        resolutions
      }
      initials
    }
    url
    isShortcut
    isMigrated
    originalImage {
      key
      status
    }
    showBrandingHeader
    noteTemplateFolderId
    isFileUpload
    fileViewerUrl
    file {
      id
      originalFilename
      ext
    }
    noteFileId
    position
    imageUrls
    image {
      key
      status
      cropPosition
    }
  }
`;

export const saveNotesPreferences = async (options) => {
  const param = {
    input: {
      filter: options.filter,
      view: options.view,
    },
  };
  const results = await client.mutate({
    mutation: gql`
      mutation saveNotesPreferences($input: SaveNotesPreferencesInput!) {
        saveNotesPreferences(input: $input) {
          preferences
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const deleteNote = async (id) => {
  const param = {
    id,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation deleteNote($id: Int!) {
        deleteNote(id: $id) {
          isDeleted
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const updateURL = async ({ id, url }) => {
  const param = {
    id,
    url,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation EditURL($id: Int!, $url: String!) {
        editUrl(id: $id, url: $url) {
          output {
            id
            weddingId
            url
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const saveTemplate = async ({
  name,
  title,
  noteHtml,
  noteJson,
  plannerOnly,
  clientReadOnly,
  noteTemplateFolderId,
  showBrandingHeader,
  isFileUpload,
  noteFileId,
  imageKey,
  url,
}) => {
  const param = {
    input: {
      name,
      title,
      noteHtml,
      noteJson,
      plannerOnly,
      clientReadOnly,
      noteTemplateFolderId: Number(noteTemplateFolderId),
      showBrandingHeader,
      isFileUpload,
      noteFileId,
      imageKey,
      url,
    },
  };
  const results = await client.mutate({
    mutation: gql`
      mutation createTemplateFromNote($input: NoteTemplateInput!) {
        createTemplateFromNote(input: $input) {
          noteTemplate {
            id
            name
            noteJson
            noteHtml
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const renameNote = async ({ id, name }) => {
  const param = {
    id: Number(id),
    name,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation RenameNote($id: Int!, $name: String!) {
        renameNote(id: $id, name: $name) {
          output {
            id
            weddingId
            title
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const getNotes = async (weddingId) => {
  const param = {
    weddingId: Number(weddingId),
  };
  const results = await client.query({
    query: gql`
      query NoteSchema($weddingId: Int) {
        notesList(weddingId: $weddingId) {
          id
          weddingId
          title
          order
          plannerOnly
          clientReadOnly
          imageUrls
          noteHtml
          noteJson
          categoryId
          groupId
          image {
            key
            status
            cropPosition
          }
          originalImage {
            key
            status
          }
          url
          isShortcut
          showBrandingHeader
          isFileUpload
          fileViewerUrl
          noteFileId
          file {
            originalFilename
          }
          blockedBy {
            id
            fullname
          }
          comments{
            id
            createdAt
            updatedAt
            kind
            body
            firstname
            lastname
            email
            html
            login{
              id
              username
              initials
              firstname
              lastname
              profileImage{
                resolutions
              }
            }
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const createNote = async ({ weddingId, title, plannerOnly, clientReadOnly, url, imageKey }) => {
  const param = {
    input: {
      weddingId: Number(weddingId),
      title,
      plannerOnly,
      clientReadOnly,
      url,
      imageKey,
    },
  };
  const results = await client.mutate({
    mutation: gql`
      mutation createNotes($input: NoteInput!) {
        createNotes(input: $input) {
          note {
            id
            categoryId
            weddingId
            url
            title
            noteHtml
            image {
              key
              status
            }
            isShortcut
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const createNoteFromTemplate = async ({ templatesId, weddingId, groupId }) => {
  const param = { templatesId, weddingId: Number(weddingId), groupId };
  const results = await client.mutate({
    mutation: gql`
      mutation createNotesFromTemplate($weddingId: Int!, $templatesId: [Int], $groupId: Int) {
        createNotesFromTemplate(weddingId: $weddingId, templatesId:$templatesId, groupId: $groupId) {
          notes {
            id
            categoryId
            weddingId
            url
            title
            noteHtml
            image {
              key
              status
            }
            isShortcut
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const duplicateNote = async ({ id }) => {
  const param = {
    id: Number(id),
  };
  const results = await client.mutate({
    mutation: gql`
      mutation duplicateNote($id: Int!) {
        duplicateNote(id: $id) {
          note {
            id
            weddingId
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const searchNotes = async ({ projectId, searchKey }) => {
  const param = {
    weddingId: Number(projectId),
    key: searchKey,
  };

  const results = await client.query({
    query: gql`
    query notesSearch($key: String, $weddingId: Int){
      notesSearch(key:$key, weddingId:$weddingId){
        id
        weddingId
        title
        order
        plannerOnly
        clientReadOnly
        imageUrls
        noteHtml
        categoryId
        groupId
        image {
          key
          status
        }
        originalImage {
          key
          status
        }
        url
        isShortcut
      }
    }
      `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;

};

export const updateNote = async ({ id, noteBody }) => {
  const param = {
    id: Number(id),
    input: {
      weddingId: Number(noteBody.weddingId),
      title: noteBody.title,
      url: noteBody.url,
      plannerOnly: noteBody.plannerOnly,
      clientReadOnly: noteBody.clientReadOnly,
      imageKey: noteBody.imageKey,
      noteHtml: noteBody.noteHtml,
      noteJson: noteBody.noteJson,
      categoryId: noteBody.categoryId,
      showBrandingHeader: noteBody.showBrandingHeader,
      noteFileId: noteBody.noteFileId,
    },
  };

  const results = await client.mutate({
    mutation: gql`
    ${NOTE_FIELDS}
    mutation updateNote($id: Int!, $input: NoteInput!){
      updateNote(id: $id, input: $input){
        note {
          ...noteInfo
        }
      }
    }`,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};


export const updateNoteContent = async ({ id, input }) => {
  const param = {
    id: Number(id),
    input,
  };

  const results = await client.mutate({
    mutation: gql`
      mutation updateNoteContent($id: Int!, $input: NoteContentInput!){
        updateNoteContent(id: $id, input: $input){
          note {
            ...noteInfo
          }
        }
      }
      fragment noteInfo on NoteSchema {
        id
        updatedAt
        updatedBy {
          id
          fullname
          profileImage {
            resolutions
          }
          initials
        }
        noteHtml
        noteJson
        blockedAt
        isBlocking
        blockedBy {
          id
          email
          fullname
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return { ...(results.data ?? {}), errors: results.errors };
};

export const pushNoteChange = async ({ id, input }) => {
  const param = {
    id: Number(id),
    input,
  };

  const results = await client.mutate({
    mutation: gql`
      mutation pushNoteContentUpdate($id: Int!, $input: PushNoteContentInput!){
        pushNoteContentUpdate(id: $id, input: $input){
          noteJson
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return { ...(results.data ?? {}), errors: results.errors };
};

export const reorderNotes = async ({ id, order, groupId }) => {
  const param = {
    id,
    order,
    groupId,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation reorderNotes($id: Int!, $order: Int!, $groupId: Int) {
        reorderNotes(id: $id, order: $order, groupId: $groupId) {
          notesOrder{
            id,
            order
          }
          weddingId
          groupId
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const restrictionsNote = async ({ id, clientReadOnly, plannerOnly }) => {
  const param = {
    id,
    clientReadOnly,
    plannerOnly,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation restrictionsNote($id: Int!, $clientReadOnly: Boolean! , $plannerOnly: Boolean!){
        restrictionsNote(id: $id, clientReadOnly: $clientReadOnly, plannerOnly: $plannerOnly){
          output{
            id
            clientReadOnly
            plannerOnly
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const fetchNoteTemplateFolders = async () => {
  const results = await client.query({
    query: gql`query noteTemplateFolderList{
      noteTemplateFolderList{
        id
        name
        position
      }
    }`,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });
  return results.data;
};

export const createNoteTemplateFolder = async ({ templateFolderName, position }) => {
  const param = {
    input: {
      name: templateFolderName,
      position,
    },
  };
  const results = await client.mutate({
    mutation: gql`
    mutation createNoteTemplateFolder($input: CreateNoteTemplateFolderInput!){
      createNoteTemplateFolder(input: $input){
        templateFolder{
          id
          name
        }
      }
    } `,
    variables: param,
    errorPolicy: 'all',
  });
  return results.data;
};

export const updateNoteTemplateFolder = async ({ id, title }) => {
  const param = {
    input: {
      id: Number(id),
      name: title,
    },
  };
  const results = await client.mutate({
    mutation: gql`
    mutation updateNoteTemplateFolder($input: UpdateNoteTemplateFolderInput!){
      updateNoteTemplateFolder(input: $input){
        templateFolder {
          id
          name
        }
      }
    } `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data.updateNoteTemplateFolder;
};

export const deleteNoteTemplateFolder = async ({ id }) => {
  const param = {
      id: Number(id),
  };
  const results = await client.mutate({
    mutation: gql`
    mutation deleteNoteTemplateFolder($id: Int!){
      deleteNoteTemplateFolder(id: $id){
        isDeleted
      }
    } `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const reorderNoteTemplateFolders = async({ id, position }) => {
  const param = {
    input: {
      id: Number(id),
      position: Number(position),
    },
  };
  const results = await client.mutate({
    mutation: gql`
    mutation orderBeforeNoteTemplateFolder($input: OrderBeforeNoteTemplateFolderInput!) {
      orderBeforeNoteTemplateFolder(input: $input){
        templateFolder {
          id
          organizationId
          position
          name
        }
      }
    } `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data.orderBeforeNoteTemplateFolder;
};

export const getNotesTemplates = async () => {
  const results = await client.query({
    query: gql`
      ${NOTE_TEMPLATE_FIELDS}
      query NoteTemplateSchema {
        notesTemplateList {
          ...noteTemplateInfo
        }
      }
    `,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const getCurrentNoteTemplate = async (id) => {
  const param = { id: Number(id) };

  const results = await client.query({
    query: gql`
      ${NOTE_TEMPLATE_FIELDS}
      query noteTemplate($id: Int) {
        noteTemplate(id: $id) {
          ...noteTemplateInfo
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const reorderNoteTemplate = async ({ id, folderId, position }) => {
  const params = {
    id: Number(id),
    folderId: Number(folderId),
    position: Number(position),
  };

  const results = await client.mutate({
    mutation: gql`
      ${NOTE_TEMPLATE_FIELDS}
      mutation reorderNoteTemplate($folderId: Int, $id: Int!, $position: Int!) {
        reorderNoteTemplate(folderId: $folderId, id: $id, position: $position) {
          noteTemplates {
            ...noteTemplateInfo
          }
        }
      }
   `,
    variables: params,
    errorPolicy: 'all',
  });

  return results.data;
};

export const createNoteTemplate = async ({
  name,
  title,
  plannerOnly,
  clientReadOnly,
  noteTemplateFolderId,
  imageKey,
  url,
}) => {
  const param = {
    input: {
      name,
      title,
      plannerOnly,
      clientReadOnly,
      noteTemplateFolderId: Number(noteTemplateFolderId),
      imageKey,
      url,
    },
  };

  const results = await client.mutate({
    mutation: gql`
      mutation createNoteTemplate($input: NoteTemplateInput!) {
        createNoteTemplate(input: $input) {
          noteTemplate {
            id
            name
            url
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const updateNoteTemplate = async ({ id, noteBody }) => {
  const params = {
    id: Number(id),
    input: {
      title: noteBody.title,
      url: noteBody.url,
      plannerOnly: noteBody.plannerOnly,
      clientReadOnly: noteBody.clientReadOnly,
      noteHtml: noteBody.noteHtml,
      noteJson: noteBody.noteJson,
      noteFileId: noteBody.noteFileId,
      isFileUpload: noteBody.isFileUpload,
      categoryId: noteBody.categoryId,
      showBrandingHeader: noteBody.showBrandingHeader,
      imageKey: noteBody.imageKey,
      name: noteBody.title,
    },
  };

  const results = await client.mutate({
    mutation: gql`
      ${NOTE_TEMPLATE_FIELDS}
      mutation updateNoteTemplate($id: Int!, $input: NoteTemplateInput!) {
        updateNoteTemplate(id: $id, input: $input) {
          noteTemplate {
            ...noteTemplateInfo
          }
        }
      }
    `,
    variables: params,
    errorPolicy: 'all',
  });

  return results.data;
};


export const updateNoteTemplateContent = async ({ id, input }) => {
  const param = {
    id: Number(id),
    input,
  };

  const results = await client.mutate({
    mutation: gql`
      mutation updateNoteTemplateContent($id: Int!, $input: NoteContentInput!){
        updateNoteTemplateContent(id: $id, input: $input){
          noteTemplate {
            id
            noteHtml
            noteJson
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return { ...(results.data ?? {}), errors: results.errors };
};

export const pushNoteTemplateChange = async ({ id, input }) => {
  const param = {
    id: Number(id),
    input,
  };

  const results = await client.mutate({
    mutation: gql`
      mutation pushNoteTemplateContentUpdate($id: Int!, $input: PushNoteContentInput!){
        pushNoteTemplateContentUpdate(id: $id, input: $input){
          noteJson
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return { ...(results.data ?? {}), errors: results.errors };
};

export const deleteNoteTemplate = async (id) => {
  const params = {
    id: Number(id),
  };

  const results = await client.mutate({
    mutation: gql`
      mutation deleteNoteTemplate($id: Int!) {
        deleteNoteTemplate(id: $id) {
          isDeleted
        }
      }
    `,
    variables: params,
    errorPolicy: 'all',
  });

  return results.data;
};

export const duplicateNoteTemplate = async ({ id }) => {
  const param = {
    id: Number(id),
  };
  const results = await client.mutate({
    mutation: gql`
      ${NOTE_TEMPLATE_FIELDS}
      mutation duplicateNoteTemplate($id: Int!) {
        duplicateNoteTemplate(id: $id) {
          noteTemplate {
            ...noteTemplateInfo
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const getNoteGroups = async ({ weddingId }) => {
  const param = {
    weddingId: Number(weddingId),
  };
  const results = await client.query({
    query: gql`
      query NotesGroupsSchema($weddingId: Int) {
        notesGroupsList(weddingId: $weddingId) {
          id
          weddingId
          name
          order
          expand
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const createNoteGroup = async ({ weddingId, groupName, lastNoteOrder, groupId }) => {
  const param = {
    weddingId: Number(weddingId),
    name: groupName,
    lastNoteOrder: Number(lastNoteOrder),
    groupId: Number(groupId),
  };
  const results = await client.mutate({
    mutation: gql`
      mutation CreateNotesGroup($weddingId: Int!, $name: String!, $lastNoteOrder: Int!, $groupId: Int!) {
        createNotesGroup(weddingId: $weddingId, name: $name, lastNoteOrder: $lastNoteOrder, groupId: $groupId) {
          notesGroups{
            id
            weddingId
            name
            order
            expand
            notes{
              id
              weddingId
              title
              order
              plannerOnly
              clientReadOnly
              imageUrls
              noteHtml
              categoryId
              groupId
              image {
                key
                status
              }
              originalImage {
                key
                status
              }
              url
              isShortcut
            }
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const renameNoteGroup = async ({ groupId, groupName }) => {
  const param = {
    id: Number(groupId),
    name: groupName,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation RenameNotesGroup($id: Int!, $name: String!) {
        renameNotesGroup(id: $id, name: $name) {
          notesGroup{
          id
            weddingId
            name
            order
          }
        }
      }
   `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const deleteNoteGroup = async ({ id }) => {
  const param = {
    id: Number(id),
  };
  const results = await client.mutate({
    mutation: gql`
      mutation DeleteNotesGroup($id: Int!) {
        deleteNotesGroup(id: $id) {
          isDeleted
          id
          notesGroupId
          groupNotes {
            id
            weddingId
            title
            order
            plannerOnly
            clientReadOnly
            imageUrls
            noteHtml
            categoryId
            groupId
            image {
              key
              status
            }
            originalImage {
              key
              status
            }
            url
            isShortcut
          }
        }
      }
   `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const reorderNoteGroup = async ({ id, groupOrder }) => {
  const param = {
    id: Number(id),
    order: groupOrder,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation ReorderNotesGroups($id: Int!, $order: Int) {
        reorderNotesGroups(id: $id, order:$order) {
          weddingId
          notesGroupsOrder{
            id
            order
          }
        }
      }
   `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};

export const updateNoteGroupsView = async ({ weddingId, noteGroupsView }) => {
  const param = {
    input: {
      weddingId: Number(weddingId),
      noteGroupsView,
    },
  };
  const results = await client.mutate({
    mutation: gql`
      mutation UpdateNotesGroupsView($input: UpdateNotesGroupsViewInput!) {
        updateNotesGroupsView(input: $input) {
          preferences
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results.data;
};


/** * Note Query/Mutation */
export const getCurrentNote = async (id) => {
  const param = { id: Number(id) };

  const results = await client.query({
    query: gql`
      ${NOTE_FIELDS}
      query note($id: Int) {
        note(id: $id) {
          ...noteInfo
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const createNoteComment = async ({ noteId, body, kind }) => {
  const param = {
    input: {
      body,
      kind,
    },
    noteId: Number(noteId),
  };
  const results = await client.mutate({
    mutation: gql`
      mutation createNoteComment($input: CommentInput, $noteId: Int!){
        createNoteComment(input: $input, noteId: $noteId){
          comment{
            id
            createdAt
            updatedAt
            kind
            body
            firstname
            lastname
            email
            html
            login{
              id
              username
              initials
              firstname
              lastname
              profileImage{
                resolutions
              }
            }
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results.data;
};

export const updateNoteComment = async ({ noteId, id, body, kind }) => {
  const param = {
    commentId: Number(id),
    input: {
      body,
      kind,
    },
    noteId: Number(noteId),
  };

  const results = await client.mutate({
    mutation: gql`
    mutation updateNoteComment($commentId: Int!, $input: CommentInput, $noteId: Int!){
      updateNoteComment(commentId: $commentId, input: $input, noteId: $noteId){
        comment{
          id
          createdAt
          updatedAt
          kind
          body
          firstname
          lastname
          email
          html
          login{
            id
            username
          }
        }
      }
    }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results.data;
};

export const deleteNoteComment = async ({ noteId, id }) => {
  const param = {
    noteId: Number(noteId),
    commentId: Number(id),
  };
  const results = await client.mutate({
    mutation: gql`
    mutation deleteNoteComment($commentId: Int!, $noteId: Int!){
      deleteNoteComment(commentId: $commentId, noteId: $noteId){
        deleted
      }
    }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results.data;
};

export const createWeddingNoteFile = async ({ filename, noteId, isTemplate = false }) => {
  const param = {
    input: {
      originalFilename: filename,
    },
    noteId: Number(noteId),
    isTemplate,
  };
  const results = await client.mutate({
    mutation: gql`
      mutation createWeddingNoteFile($input: CreateWeddingNoteFileInput!, $noteId: Int!, $isTemplate: Boolean!) {
        createWeddingNoteFile(input: $input, noteId: $noteId, isTemplate: $isTemplate) {
          file {
            id
            key
            originalFilename
            ext
            contentType
            label
            isStored
            storageKeyOverride
          }
          form {
            action
            fields {
              name
              value
            }
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
  });

  return results;
};

export const confirmWeddingNoteFileUpload = async ({ key }) => {
  const results = await client.mutate({
    mutation: gql`
      mutation confirmWeddingNoteFileUpload($key: String!) {
        confirmWeddingNoteFileUpload(key: $key) {
          file {
            id
            key
            originalFilename
            ext
            contentType
            label
            isStored
            storageKeyOverride
          }
        }
      }
    `,
    variables: { key },
    errorPolicy: 'all',
  });

  return results;
};

export const blockingWeddingNote = async (id) => {
  const results = await client.mutate({
    mutation: gql`
      mutation blockingWeddingNote($noteId: Int!){
        blockingWeddingNote(noteId: $noteId){
          isBlocking
          blockedBy{
            id
            email
            fullname
          }
        }
      }
    `,
    variables: {
      noteId: Number(id),
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results.data;
};

export const unblockingWeddingNote = async (id) => {
  const results = await client.mutate({
    mutation: gql`
      mutation unblockingWeddingNote($noteId: Int!){
        unblockingWeddingNote(noteId: $noteId){
          isBlocking
        }
      }
    `,
    variables: {
      noteId: Number(id),
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results.data;
};


export const getNoteBlockedStatus = async (id) => {
  const param = { id: Number(id) };

  const results = await client.query({
    query: gql`
      query note($id: Int) {
        note(id: $id) {
          isBlocking
          blockedBy {
            id
            fullname
          }
          noteJson
          updatedAt
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const deleteWeddingNoteFile = async (id) => {
  const param = {
    noteId: Number(id),
  };
  const results = await client.mutate({
    mutation: gql`
      ${NOTE_FIELDS}
      mutation deleteWeddingNoteFile($noteId: Int!) {
        deleteWeddingNoteFile(noteId: $noteId) {
          note {
            ...noteInfo
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const deleteTemplateNoteFile = async (id) => {
  const param = {
    templateId: Number(id),
  };
  const results = await client.mutate({
    mutation: gql`
      ${NOTE_TEMPLATE_FIELDS}
      mutation deleteTemplateNoteFile($templateId: Int!) {
        deleteTemplateNoteFile(templateId: $templateId) {
          noteTemplate {
            ...noteTemplateInfo
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const getPlanningCategory = async (id) => {
  const param = { id: Number(id) };

  const results = await client.query({
    query: gql`
      query planningCategory($id: Int) {
        planningCategory(id: $id) {
          id
          category
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const getEmailBranding = async (orgId) => {
  const param = { orgId: Number(orgId) };

  const results = await client.query({
    query: gql`
      query emailBranding($orgId: Int!) {
        emailBranding(orgId: $orgId) {
          id
          logo {
            key
            resolutions
          }
          templateDict
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const getPageBranding = async (orgId) => {
  const param = { orgId: Number(orgId) };

  const results = await client.query({
    query: gql`
      query pageBranding($orgId: Int!) {
        pageBranding(orgId: $orgId) {
          id
          logo {
            key
            resolutions
          }
          templateDict
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const getStyleGuideList = async (weddingId) => {
  const param = { weddingId: Number(weddingId) };

  const results = await client.query({
    query: gql`
      query styleGuidesList($weddingId: Int) {
        styleGuidesList(weddingId: $weddingId) {
          id
          name
          images {
            isHeroImage
            imageUrls
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const getColorPalettes = async (weddingId) => {
  const param = { weddingId: Number(weddingId) };

  const results = await client.query({
    query: gql`
      query weddingPalettesList($weddingId: Int) {
        weddingPalettesList(weddingId: $weddingId) {
          name
          id
          palette {
            colors
          }
        }
      }
    `,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const updateUseFileUpload = async ({ id, useFile }) => {
  const param = { noteId: Number(id), useFile };

  const results = await client.mutate({
    mutation: gql`
      mutation UseFileUpload($noteId: Int!, $useFile: Boolean!){
        useFileUpload(noteId:$noteId, useFile:$useFile){
          output{
            id
            weddingId
            title
            order
            plannerOnly
            clientReadOnly
            imageUrls
            noteHtml
            noteJson
            categoryId
            groupId
            image {
              key
              status
              cropPosition
            }
            originalImage {
              key
              status
            }
            url
            isShortcut
            showBrandingHeader
            isFileUpload
            fileViewerUrl
            noteFileId
            blockedBy {
              id
              fullname
            }
            comments{
              id
              createdAt
              updatedAt
              kind
              body
              firstname
              lastname
              email
              html
              login{
                id
                username
                initials
                firstname
                lastname
                profileImage{
                  resolutions
                }
              }
            }
          }
        }
      }`,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results.data;
};

export const updateUseTemplateFileUpload = async ({ id, useFile }) => {
  const param = { templateId: Number(id), useFile };

  const results = await client.mutate({
    mutation: gql`
      ${NOTE_TEMPLATE_FIELDS}
      mutation UseTemplateFileUpload($templateId: Int!, $useFile: Boolean!){
        useTemplateFileUpload(templateId: $templateId, useFile: $useFile){
          output {
            ...noteTemplateInfo
          }
        }
      }`,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results.data;
};

export const migrateNote = async ({ id, noteContent }) => {
  const param = { id: Number(id), noteJson: { currentVersion: noteContent } };

  const results = await client.mutate({
    mutation: gql`
      mutation MigrateNote($id: Int!, $noteJson: JSON!){
        migrateNote(id: $id, noteJson: $noteJson){
          note {
            id
            title
            noteJson
            isMigrated
          }
        }
      }`,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};

export const migrateNoteTemplate = async ({ id, noteContent }) => {
  const param = { id: Number(id), noteJson: { currentVersion: noteContent } };

  const results = await client.mutate({
    mutation: gql`
      mutation MigrateNoteTemplate($id: Int!, $noteJson: JSON!){
        migrateNoteTemplate(id: $id, noteJson: $noteJson){
          noteTemplate {
            id
            title
            noteJson
            isMigrated
          }
        }
      }`,
    variables: param,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return results;
};
